import { BaseQueryFn, FetchArgs, FetchBaseQueryError, FetchBaseQueryMeta } from '@reduxjs/toolkit/dist/query';
import { MutationLifecycleApi, TagDescription } from '@reduxjs/toolkit/dist/query/endpointDefinitions';
import {
    ExpenseCreateDto,
    ExpenseDetails,
    ExpenseExportListItemDto,
    ExpenseGroupedQueryResponse,
    ExpenseListItem,
    ExpensesSwitchToMatterDto,
    ExpensesTotalsDto,
    ExpenseUpdateDto,
    UpdateExpensesBillability,
} from 'types/expenses';
import { PaginationProps } from 'types/paginationAndFilter';
import { objectToQueryParams } from 'utils/linkUtil';
import { QueryParams } from './common/types';
import { APIMattersSlice } from './matters.ts';
import { injectCountEndPoint, portalAPI } from './portalAPI';
import { accumulatedQueryWithFilters } from './util/endpointBuilder';

const paginationQueryEndpoints = ['getExpensesWithFilters'] as const;

/**
 * Manually updates given expense in list caches
 */
const updateExpenseInLists = async (
    expense: ExpenseDetails,
    api: MutationLifecycleApi<
        ExpenseUpdateDto,
        BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError, Record<string, unknown>, FetchBaseQueryMeta>,
        ExpenseDetails,
        'portalAPI'
    >
) => {
    const queries = api.getState().portalAPI.queries;
    paginationQueryEndpoints.forEach((endpointName) => {
        const endpointQueries = Object.entries(queries)
            .filter((entry) => entry[1]?.endpointName === endpointName)
            .map((entry) => entry[1]);
        endpointQueries.forEach((query) => {
            api.dispatch(
                APIExpensesSlice.util.updateQueryData(endpointName, query?.originalArgs as PaginationProps, (draft) => {
                    const item = draft.content.find((item) => item.id === expense.id);
                    if (item !== undefined) {
                        // `timecard` is intentionally stripped from the assignment.
                        const { timecard, ...restExpense } = expense;
                        Object.assign(item, restExpense);
                    }
                })
            );
        });
    });
};

/**
 *
 * Manually updates given expense in getExpenseById cache
 */
const updateGetExpenseByIdCache = (
    expense: ExpenseDetails,
    api: MutationLifecycleApi<
        ExpenseUpdateDto,
        BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError, Record<string, unknown>, FetchBaseQueryMeta>,
        ExpenseDetails,
        'portalAPI'
    >
) => {
    api.dispatch(
        APIExpensesSlice.util.updateQueryData('getExpenseById', expense.id, (draft) =>
            Object.assign(draft || {}, expense)
        )
    );
};

type TagTypes = 'Expenses' | 'ExpensesTotals' | 'ExpensesListItem' | 'Timecards' | 'ExpensesCount';

export const APIExpensesSlice = portalAPI
    .enhanceEndpoints<TagTypes>({
        addTagTypes: ['Expenses', 'ExpensesTotals', 'ExpensesListItem', 'Timecards', 'ExpensesCount'],
    })
    .injectEndpoints({
        endpoints: (builder) => ({
            getExpenseById: builder.query<ExpenseDetails, number | string>({
                query: (id) => `/v1/expenses/${id}`,
                keepUnusedDataFor: 0,
                providesTags: (_1, _2, arg) => [{ type: 'Expenses', id: arg }],
            }),
            getExpensesList: builder.query<ExpenseListItem[], void>({
                query: () => '/v1/expenses',
                providesTags: ['Expenses'],
            }),
            getExpensesWithFilters: accumulatedQueryWithFilters<ExpenseListItem>(builder, '/v1/expenses', {
                providesTags: ['ExpensesListItem'],
            }),
            getExpensesGrouped: builder.query<ExpenseGroupedQueryResponse, PaginationProps>({
                // The list table's LazyQueryTrigger always sends PaginationProps (filters +
                // page/size/sort). The grouped endpoint only consumes filters/passiveFilters —
                // page/size/sort are ignored because the BE returns the entire filtered tree as
                // a single page. We accept the wide PaginationProps to preserve the
                // LazyQueryTrigger contract but destructure only what we use.
                query: ({ filters = '', passiveFilters = '' } = {}) => {
                    const filter = passiveFilters
                        ? [passiveFilters, filters].filter((s) => s && s.length > 0).join(';')
                        : filters;
                    const params = new URLSearchParams();
                    if (filter) params.set('filter', filter);
                    const qs = params.toString();
                    return `/v1/expenses/grouped${qs ? `?${qs}` : ''}`;
                },
                providesTags: ['ExpensesListItem'],
            }),
            getExpensesTotals: builder.query<ExpensesTotalsDto, { filters?: string }>({
                query: ({ filters = '' }) =>
                    `/v1/expenses/calculate-totals${objectToQueryParams({ filter: filters || undefined })}`,
                providesTags: ['ExpensesTotals'],
                keepUnusedDataFor: 0,
            }),
            getExpensesExportList: builder.query<ExpenseExportListItemDto[], QueryParams>({
                query: ({ filter = '', sort = '' }) => ({
                    url: `/v1/expenses/export${objectToQueryParams({ filter, sort })}`,
                    method: 'GET',
                }),
            }),
            getExpensesCount: injectCountEndPoint(builder, '/v1/expenses', 'ExpensesCount'),
            addExpense: builder.mutation<ExpenseDetails, ExpenseCreateDto>({
                query: ({ ...body }) => ({
                    url: `/v1/expenses`,
                    method: 'POST',
                    body,
                }),
                invalidatesTags: (_result, _error, expense) => [
                    'Expenses',
                    'ExpensesListItem',
                    'ExpensesTotals',
                    { type: 'Timecards', id: expense.timecardId || undefined },
                ],
                async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                    await queryFulfilled;
                    if (arg.matterId) {
                        dispatch(APIMattersSlice.util.invalidateTags([{ type: 'Matter', id: String(arg.matterId) }]));
                    }
                },
            }),
            updateExpense: builder.mutation<ExpenseDetails, ExpenseUpdateDto>({
                query: ({ id, ...body }) => ({
                    url: `/v1/expenses/${id}`,
                    method: 'PUT',
                    body,
                }),
                async onQueryStarted(arg, api) {
                    const { data: updatedExpense } = await api.queryFulfilled;
                    updateExpenseInLists(updatedExpense, api);
                    updateGetExpenseByIdCache(updatedExpense, api);
                    if (arg.matterId) {
                        api.dispatch(
                            APIMattersSlice.util.invalidateTags([{ type: 'Matter', id: String(arg.matterId) }])
                        );
                    }
                },
                invalidatesTags: (_1, _2, arg) =>
                    arg.id
                        ? ['Expenses', 'ExpensesListItem', 'ExpensesTotals', { type: 'Expenses', id: arg.id }]
                        : ['Expenses', 'ExpensesListItem', 'ExpensesTotals'],
            }),
            deleteExpense: builder.mutation<boolean, { id: number }>({
                query: ({ id }) => ({
                    url: `/v1/expenses/${id}`,
                    method: 'DELETE',
                }),
                invalidatesTags: ['Expenses', 'ExpensesListItem', 'ExpensesTotals'],
                async onQueryStarted(_, { dispatch, queryFulfilled }) {
                    await queryFulfilled;
                    dispatch(APIMattersSlice.util.invalidateTags(['Matter']));
                },
            }),
            switchExpensesMatter: builder.mutation<void, ExpensesSwitchToMatterDto>({
                query: ({ ...body }) => ({
                    url: `/v1/expenses/switch-matter`,
                    method: 'PUT',
                    body,
                }),
                invalidatesTags: (_1, _2, arg) => {
                    const expenseIdTags: TagDescription<TagTypes>[] = arg.expenseIds.map((id) => ({
                        type: 'Expenses',
                        id,
                    }));
                    const result: TagDescription<TagTypes>[] = [
                        'Expenses',
                        'ExpensesListItem',
                        'ExpensesTotals',
                        ...expenseIdTags,
                    ];
                    return result;
                },
                async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                    await queryFulfilled;
                    dispatch(
                        APIMattersSlice.util.invalidateTags([
                            { type: 'Matter', id: String(arg.currentMatterId) },
                            { type: 'Matter', id: String(arg.newMatterId) },
                        ])
                    );
                },
            }),
            updateExpensesBillability: builder.mutation<void, UpdateExpensesBillability>({
                query: (body) => ({
                    url: `/v1/expenses/billability`,
                    method: 'PUT',
                    body,
                }),
                invalidatesTags: (_1, _2, arg) => {
                    const expenseIdTags: TagDescription<TagTypes>[] = arg.expenseIds.map((id) => ({
                        type: 'Expenses',
                        id,
                    }));
                    const result: TagDescription<TagTypes>[] = [
                        'Expenses',
                        'ExpensesListItem',
                        'ExpensesTotals',
                        ...expenseIdTags,
                    ];
                    return result;
                },
                async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                    await queryFulfilled;
                    dispatch(APIMattersSlice.util.invalidateTags([{ type: 'Matter', id: String(arg.matterId) }]));
                },
            }),
        }),
        overrideExisting: false,
    });

export const {
    useGetExpenseByIdQuery,
    useGetExpensesWithFiltersQuery,
    useLazyGetExpensesWithFiltersQuery,
    useLazyGetExpensesGroupedQuery,
    useGetExpensesTotalsQuery,
    useLazyGetExpensesExportListQuery,
    useLazyGetExpensesCountQuery,
    useAddExpenseMutation,
    useUpdateExpenseMutation,
    useDeleteExpenseMutation,
    useSwitchExpensesMatterMutation,
    useUpdateExpensesBillabilityMutation,
} = APIExpensesSlice;
